import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react';
import { ContentChannel } from '@mg/shared';
import type { SocialPlatform, ThreadConversion, ThreadConversionChange } from '@mg/shared';
import { useDialogs } from '../../components/ui/DialogProvider';
import { ApiRequestError, api } from '../../lib/api';
import { newBlockKey, newTextBlock, type EditorBlock } from './blocks';
import {
  applyConversion,
  conversionEdited,
  conversionInForm,
  conversionOffer,
  reviewChanges,
  undoConversion,
  usableConversion,
  type ConversionOffer,
  type ConversionPlan,
  type ConversionReview,
} from './threadConversion';

/**
 * How long the composer waits for a conversion before it shows the error card: the server's own
 * deadline for the whole request (150 s, or 210 s for a text converted in parts) and 15 s more, so a
 * slow conversion normally ends with the server's answer rather than this timer.
 */
export const CLIENT_TIMEOUT_MS = 225_000;

const UNDO_CONFIRM = {
  title: 'Undo the conversion?',
  message:
    'Your changes to the thread will be lost, and the original text goes back into a single post.',
  confirmLabel: 'Undo and restore',
  danger: true,
};

const REGENERATE_CONFIRM = {
  title: 'Regenerate the thread?',
  message: 'Your changes to the thread will be lost. AI will convert the original text again.',
  confirmLabel: 'Regenerate',
  danger: true,
};

/** The card under the single post's text: the suggestion, the progress, or the error. */
export interface ThreadConversionCard {
  state: 'offer' | 'converting' | 'failed';
  platform: SocialPlatform;
  /** The text as the platform counts it, and its limit: "N characters over the X limit of L". */
  count: number;
  limit: number;
  /** For an error the server explained (a 400 or 503), its sentence: why Retry will not help. */
  detail: string | null;
}

/** The banner over the content while a conversion is in review. */
export interface ThreadConversionBanner {
  /** How many posts the AI made. */
  posts: number;
  /** The platform the AI converted FOR, whose limit the banner names - even after a switch. */
  platform: SocialPlatform;
  showChanges: boolean;
  /** New once per conversion, unique app-wide: the banner moves focus to its title per value. */
  focusToken: number;
}

export interface ThreadConversionControls {
  card: ThreadConversionCard | null;
  banner: ThreadConversionBanner | null;
  /**
   * While Show changes is on: the changes each of the AI's posts shows, by the key of its text box
   * (null: drawn as plain text). Undefined while it is off, and with no review.
   */
  changesShown: ReadonlyMap<string, ThreadConversionChange[] | null> | undefined;
  /** A conversion is in flight: the text box is read-only, and the composer is busy. */
  converting: boolean;
  convert(): void;
  cancel(): void;
  retry(): void;
  keepSingle(): void;
  undo(): Promise<void>;
  regenerate(): Promise<void>;
  dismiss(): void;
  setShowChanges(on: boolean): void;
  /** The post was saved: the review ends, and the original text and the highlights go with it. */
  end(): void;
}

type Conversion =
  | { phase: 'idle' }
  | { phase: 'converting'; id: number; platform: SocialPlatform; offer: ConversionOffer }
  | { phase: 'failed'; platform: SocialPlatform; text: string; detail: string | null }
  | ({ phase: 'review'; id: number; platform: SocialPlatform; showChanges: boolean } &
      ConversionReview);

const IDLE: Conversion = { phase: 'idle' };

/**
 * Numbers every conversion asked for, in every composer. An answer is used only while its number is
 * the one in flight, and a review's number is its banner's focus token - unique app-wide, so a
 * banner in a composer opened later can never take an earlier conversion's token for its own.
 */
let lastConversion = 0;

/** The server's own sentence where it says why trying again cannot help (400, 503); else none. */
function failureDetail(error: unknown): string | null {
  return error instanceof ApiRequestError && (error.status === 400 || error.status === 503)
    ? error.message
    : null;
}

/**
 * "Convert to thread with AI" in the composer (spec sections 1-4, 6, 7): the suggestion card, the
 * request, the review of its result, and the way back. It all lives in the composer: the editor is
 * unmounted on every tab switch, and the modal unmounting the composer is exactly "until the modal
 * is closed". Nothing is stored, and nothing is sent but the request.
 *
 * The suggestion card is DERIVED on every render ({@link conversionOffer}), so a platform switch,
 * an edit or a paste shows, hides or renumbers it with no state of its own. What is kept is the
 * phase - a conversion in flight (its request, abort controller and timer), one that failed, or
 * one in review - and whether the person chose "Keep as a single post", which holds until the
 * composer unmounts.
 *
 * A Cancel, the timer, a newer request, a platform switch and an unmount all end a request the same
 * way: it is forgotten first, then aborted, so whatever it answers afterwards is ignored. A Cancel
 * is told from a failure by that, never by the error - an aborted request rejects with the same
 * network error as a dropped connection.
 */
export function useThreadConversion(a: {
  blocks: EditorBlock[];
  setBlocks: Dispatch<SetStateAction<EditorBlock[]>>;
  /** The destination the counters judge by - never the composer's placeholder platform. */
  platform: SocialPlatform | null;
  channel: ContentChannel;
  readOnly: boolean;
}): ThreadConversionControls {
  const { blocks, platform, channel, readOnly } = a;
  const { confirm } = useDialogs();
  const [conversion, setConversion] = useState<Conversion>(IDLE);
  const [dismissed, setDismissed] = useState(false);

  // What a handler reads when it runs: an answer, the timer or a confirm comes back after the
  // render that drew the button.
  const latest = useRef({ ...a, confirm });
  latest.current = { ...a, confirm };
  /** The phase as the last change left it - ahead of the render that will show it. */
  const state = useRef<Conversion>(IDLE);
  const dismissedRef = useRef(false);
  const flight = useRef<{
    id: number;
    controller: AbortController;
    timer: ReturnType<typeof setTimeout>;
  } | null>(null);

  const to = useCallback((next: Conversion) => {
    state.current = next;
    setConversion(next);
  }, []);
  /** Forget the request in flight: its answer, its failure and its timer are ignored from now. */
  const settle = useCallback(() => {
    const request = flight.current;
    flight.current = null;
    if (request) clearTimeout(request.timer);
    return request;
  }, []);
  /** Forget it and abort it, so the server stops paying for an answer nobody will read. */
  const stop = useCallback(() => settle()?.controller.abort(), [settle]);

  const landed = useCallback(
    (id: number, target: SocialPlatform, offer: ConversionOffer, answer: ThreadConversion) => {
      settle();
      if (!usableConversion(answer)) {
        to({ phase: 'failed', platform: target, text: offer.text, detail: null });
        return;
      }
      const [first, ...others] = answer.posts;
      // Keys are made here, never in the updater: it runs twice under StrictMode.
      const plan: ConversionPlan = {
        textKey: offer.textKey,
        original: offer.text,
        firstKey: newBlockKey(),
        firstText: first.text,
        rest: others.map((p) => ({ ...newTextBlock(p.text), threadKey: newBlockKey() })),
      };
      const { blocks: now, setBlocks } = latest.current;
      const textBlock = now.find((b) => b.key === offer.textKey);
      // The text changed while the request was away (the AI assist, the post read again): these
      // posts were made from a text that is not there. Nothing failed - the card is derived again.
      if (!textBlock || applyConversion(now, plan) === now) {
        to(IDLE);
        return;
      }
      setBlocks((prev) => applyConversion(prev, plan));
      to({
        phase: 'review',
        id,
        platform: target,
        showChanges: true,
        textKey: offer.textKey,
        textBlock: { ...textBlock },
        original: offer.text,
        posts: [
          { key: offer.textKey, text: first.text, changes: first.changes },
          ...plan.rest.map((b, i) => ({ key: b.key, text: b.text, changes: others[i].changes })),
        ],
      });
    },
    [settle, to],
  );

  /** Ask for a conversion of the offered text, for `target`'s limit. */
  const start = useCallback(
    (offer: ConversionOffer, target: SocialPlatform) => {
      stop();
      const id = (lastConversion += 1);
      const controller = new AbortController();
      const timer = setTimeout(() => {
        if (flight.current?.id !== id) return;
        stop();
        to({ phase: 'failed', platform: target, text: offer.text, detail: null });
      }, CLIENT_TIMEOUT_MS);
      flight.current = { id, controller, timer };
      to({ phase: 'converting', id, platform: target, offer });
      void api.convertToThread({ platform: target, text: offer.text }, controller.signal).then(
        (answer) => {
          if (flight.current?.id === id) landed(id, target, offer, answer);
        },
        (error: unknown) => {
          if (flight.current?.id !== id) return;
          settle();
          to({ phase: 'failed', platform: target, text: offer.text, detail: failureDetail(error) });
        },
      );
    },
    [landed, settle, stop, to],
  );

  /** Start from the card: the text the box holds now, for the destination chosen now. */
  const startFromCard = useCallback(() => {
    const now = latest.current;
    const offer = conversionOffer(now);
    if (offer && now.platform && !dismissedRef.current) start(offer, now.platform);
  }, [start]);

  const convert = useCallback(() => {
    const { phase } = state.current;
    if (phase === 'idle' || phase === 'failed') startFromCard();
  }, [startFromCard]);

  const retry = useCallback(() => {
    if (state.current.phase === 'failed') startFromCard();
  }, [startFromCard]);

  const cancel = useCallback(() => {
    if (state.current.phase !== 'converting') return;
    stop();
    to(IDLE);
  }, [stop, to]);

  const keepSingle = useCallback(() => {
    const { phase } = state.current;
    if (phase !== 'idle' && phase !== 'failed') return;
    dismissedRef.current = true;
    setDismissed(true);
    to(IDLE);
  }, [to]);

  /**
   * The review that is on, once `question` has been asked and answered yes where the thread was
   * edited since the conversion - or null: no review, the answer was no, or it ended meanwhile.
   */
  const confirmedReview = useCallback(async (question: typeof UNDO_CONFIRM) => {
    const review = state.current;
    if (review.phase !== 'review') return null;
    const { blocks: now, confirm: ask } = latest.current;
    if (conversionEdited(now, review) && !(await ask(question))) return null;
    const after = state.current;
    return after.phase === 'review' && after.id === review.id ? after : null;
  }, []);

  const undo = useCallback(async () => {
    const review = await confirmedReview(UNDO_CONFIRM);
    if (!review) return;
    latest.current.setBlocks((prev) => undoConversion(prev, review));
    // The single post is back, over its limit, so the suggestion card shows again: a review never
    // follows "Keep as a single post", which no conversion can start after.
    to(IDLE);
  }, [confirmedReview, to]);

  const regenerate = useCallback(async () => {
    const review = await confirmedReview(REGENERATE_CONFIRM);
    if (!review) return;
    const now = latest.current;
    now.setBlocks((prev) => undoConversion(prev, review));
    // The ORIGINAL text, back in the single post, converted again for the destination chosen NOW -
    // the person may have switched between X and Threads. Where it fits that destination as one
    // post there is nothing to convert, and the Undo is all that happens.
    const offer = conversionOffer({ ...now, blocks: undoConversion(now.blocks, review) });
    if (offer && now.platform) start(offer, now.platform);
    else to(IDLE);
  }, [confirmedReview, start, to]);

  const dismiss = useCallback(() => {
    if (state.current.phase === 'review') to(IDLE);
  }, [to]);

  const setShowChanges = useCallback(
    (on: boolean) => {
      const review = state.current;
      if (review.phase === 'review' && review.showChanges !== on) {
        to({ ...review, showChanges: on });
      }
    },
    [to],
  );

  // A conversion is for one platform's limit, on a post that can change: a switch to another
  // platform (not another account of the same one), a type that is not Social, or a post that has
  // become read-only cancels it, as Cancel does.
  useEffect(() => {
    if (conversion !== state.current || conversion.phase !== 'converting') return;
    if (platform !== conversion.platform || channel !== ContentChannel.SOCIAL || readOnly) {
      stop();
      to(IDLE);
    }
  }, [conversion, platform, channel, readOnly, stop, to]);

  // The review lasts while the form holds the thread it made: cut back to one post, or never
  // applied (the text changed under the answer), there is nothing left to review.
  const inForm = conversion.phase === 'review' && conversionInForm(blocks, conversion);
  useEffect(() => {
    if (conversion === state.current && conversion.phase === 'review' && !inForm) to(IDLE);
  }, [conversion, inForm, to]);

  // The composer unmounts - the modal closed - with a request in flight: nobody reads its answer.
  useEffect(() => () => stop(), [stop]);

  const offer = useMemo(
    () => conversionOffer({ channel, platform, readOnly, blocks }),
    [channel, platform, readOnly, blocks],
  );

  let card: ThreadConversionCard | null = null;
  if (conversion.phase === 'converting') {
    const { count, limit } = conversion.offer;
    card = { state: 'converting', platform: conversion.platform, count, limit, detail: null };
  } else if (
    (conversion.phase === 'idle' || conversion.phase === 'failed') &&
    offer &&
    platform &&
    !dismissed
  ) {
    // The error card shows while the box holds the text that failed, for the platform it failed
    // for; a new text deserves the offer, not an old error.
    const failed =
      conversion.phase === 'failed' &&
      conversion.text === offer.text &&
      conversion.platform === platform
        ? conversion
        : null;
    card = {
      state: failed ? 'failed' : 'offer',
      platform,
      count: offer.count,
      limit: offer.limit,
      detail: failed ? failed.detail : null,
    };
  }

  const review = conversion.phase === 'review' && inForm && !readOnly ? conversion : null;
  const banner: ThreadConversionBanner | null = review
    ? {
        posts: review.posts.length,
        platform: review.platform,
        showChanges: review.showChanges,
        focusToken: review.id,
      }
    : null;
  const showing = review?.showChanges ? review : null;
  const changesShown = useMemo(
    () => (showing ? reviewChanges(blocks, showing) : undefined),
    [showing, blocks],
  );

  return {
    card,
    banner,
    changesShown,
    converting: conversion.phase === 'converting',
    convert,
    cancel,
    retry,
    keepSingle,
    undo,
    regenerate,
    dismiss,
    setShowChanges,
    end: dismiss,
  };
}
